import { Link } from 'react-router-dom';
import { FiArchive, FiSearch, FiAward, FiUsers, FiArrowRight } from 'react-icons/fi';

/**
 * Página informativa de Historicidad Bíblica (para acceder desde el Home)
 */
export default function HistoricidadInfo() {
  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl shadow-xl p-8 mb-8 text-white">
          <div className="flex items-center gap-4 mb-4">
            <FiArchive className="w-12 h-12" />
            <h1 className="text-4xl font-bold">
              Historicidad Bíblica
            </h1>
          </div>
          <p className="text-xl text-purple-50 mb-6">
            Explora datos históricos verificados sobre eventos, personajes y lugares bíblicos con análisis de autenticidad.
          </p>
          
          <Link
            to="/historicidad"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-colors shadow-lg"
          >
            Explorar Datos Históricos
            <FiArrowRight className="w-5 h-5" />
          </Link>
        </div>

        {/* Características */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <FiArchive className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Datos Verificados
            </h3>
            <p className="text-gray-600">
              Información histórica curada y verificada por expertos en arqueología e historia bíblica.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <FiAward className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Análisis de Autenticidad
            </h3>
            <p className="text-gray-600">
              Cada dato incluye porcentajes de historicidad, autenticidad y veracidad basados en evidencia.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <FiSearch className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Búsqueda Avanzada
            </h3>
            <p className="text-gray-600">
              Filtra por catálogos: Lugares, Personajes, Arqueología, Reinos, y más. Ordena por relevancia o fecha.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <FiUsers className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Colaboración Comunitaria
            </h3>
            <p className="text-gray-600">
              Los usuarios registrados pueden proponer nuevos datos que pasan por un proceso de validación riguroso.
            </p>
          </div>
        </div>

        {/* Catálogos Disponibles */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Catálogos Disponibles
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-purple-900 mb-1">🏛️ Lugares y Geografía</h4>
              <p className="text-sm text-gray-600">Ciudades, montes, ríos y regiones mencionadas en la Biblia</p>
            </div>

            <div className="border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-purple-900 mb-1">👥 Personajes Históricos</h4>
              <p className="text-sm text-gray-600">Reyes, profetas y figuras clave con evidencia histórica</p>
            </div>

            <div className="border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-purple-900 mb-1">🏺 Arqueología</h4>
              <p className="text-sm text-gray-600">Descubrimientos arqueológicos que confirman relatos bíblicos</p>
            </div>

            <div className="border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-purple-900 mb-1">👑 Reinos y Naciones</h4>
              <p className="text-sm text-gray-600">Imperios, reinos y pueblos antiguos del Medio Oriente</p>
            </div>

            <div className="border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-purple-900 mb-1">⚔️ Batallas y Eventos</h4>
              <p className="text-sm text-gray-600">Conflictos bélicos y eventos históricos documentados</p>
            </div>

            <div className="border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-purple-900 mb-1">📜 Manuscritos</h4>
              <p className="text-sm text-gray-600">Documentos antiguos y evidencia textual</p>
            </div>
          </div>
        </div>

        {/* Proceso de Validación */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Proceso de Validación
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                1
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Propuesta Inicial</h4>
                <p className="text-gray-600 text-sm">Los usuarios registrados pueden proponer nuevos datos históricos con fuentes y referencias.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                2
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Revisión por Editores</h4>
                <p className="text-gray-600 text-sm">Los editores verifican la información, completan datos faltantes y evalúan las fuentes.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                3
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Validación por Expertos</h4>
                <p className="text-gray-600 text-sm">Los validadores (expertos en historia) revisan y asignan porcentajes de historicidad.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                4
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Aprobación Final</h4>
                <p className="text-gray-600 text-sm">Los gestores aprueban la información para publicación pública en la plataforma.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Funcionalidades */}
        <div className="bg-white rounded-xl shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Funcionalidades Disponibles
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Explorar Datos Publicados</h4>
                <p className="text-gray-600 text-sm">Accede a todos los datos históricos aprobados y publicados. Cada uno incluye descripción, fuentes y análisis de autenticidad.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Filtrar por Catálogo</h4>
                <p className="text-gray-600 text-sm">Usa los filtros para encontrar información específica: lugares, personajes, arqueología, etc.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Ordenar por Relevancia</h4>
                <p className="text-gray-600 text-sm">Ordena por más recientes, más vistos o mayor porcentaje de historicidad.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Vista Detallada</h4>
                <p className="text-gray-600 text-sm">Haz clic en cualquier dato para ver la información completa, fuentes, referencias y análisis de expertos.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Acceso Sin Registro</h4>
                <p className="text-gray-600 text-sm">Todos los datos publicados están disponibles para lectura sin necesidad de crear una cuenta.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Proponer Nuevos Datos (Usuarios Registrados)</h4>
                <p className="text-gray-600 text-sm">Los usuarios registrados pueden contribuir proponiendo nuevos datos históricos para validación.</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Final */}
        <div className="mt-8 text-center">
          <Link
            to="/historicidad"
            className="inline-flex items-center gap-2 px-8 py-4 bg-purple-600 text-white rounded-lg font-semibold text-lg hover:bg-purple-700 transition-colors shadow-lg"
          >
            <FiArchive className="w-5 h-5" />
            Explorar Historicidad
          </Link>
        </div>
      </div>
    </div>
  );
}






